import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Navigation } from './Navigation'
import { NotificationBell } from './NotificationBell'
import { GlobalSearch } from './GlobalSearch'
import { MobileBottomNav } from './MobileBottomNav'
import { CommandPalette } from './CommandPalette'
import { TrialBanner } from './common/TrialBanner'
import { EmailVerificationBanner } from './common/EmailVerificationBanner'
import { NpsPrompt } from './nps/NpsPrompt'
import { StripeHealthBanner } from './admin/StripeHealthBanner'

// Phase 2 — Review collection URLs. Pulled from Vite env so they can be
// flipped per environment without code changes (dev/staging may not have
// real G2 listings yet).
const G2_REVIEW_URL =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env
    .VITE_G2_REVIEW_URL || undefined
const CAPTERRA_REVIEW_URL =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env
    .VITE_CAPTERRA_REVIEW_URL || undefined

export function MainLayout() {
  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Skip-to-content link — first focusable element for keyboard/SR users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Sidebar — hidden on mobile, shown on lg+ */}
      <div className="hidden lg:block">
        <Navigation isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      </div>

      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        {/* Trial / billing banner — shown when org is on trial or expired */}
        <TrialBanner />

        {/* Sprint 30 — verify-your-email nudge for unverified accounts */}
        <EmailVerificationBanner />

        {/* Superuser-only nag if Stripe isn't ready to take real money */}
        <StripeHealthBanner />

        {/* Top header */}
        <header role="banner" className="h-12 flex-shrink-0 bg-[#1a2942] border-b border-[#3D4F61] flex items-center justify-end gap-2 px-4 z-40">
          <GlobalSearch />
          <NotificationBell />
        </header>

        {/* Main content — extra bottom padding on mobile for the bottom nav bar */}
        <main
          id="main-content"
          tabIndex={-1}
          aria-label="Main content"
          className="flex-1 overflow-y-auto pb-16 lg:pb-0"
        >
          <Outlet />
        </main>
      </div>

      {/* Bottom nav — mobile only */}
      <MobileBottomNav />

      {/* A11.4 — command palette + keyboard shortcut layer */}
      <CommandPalette />

      {/* Part 19 — NPS prompt; self-suppresses when user isn't due. */}
      <NpsPrompt
        g2ReviewUrl={G2_REVIEW_URL}
        capterraReviewUrl={CAPTERRA_REVIEW_URL}
      />
    </div>
  )
}
